import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationCircle,
  faExclamationTriangle,
  faInfoCircle,
  faCheckSquare,
  faSquare,
  faList,
  faGlobe
} from '@fortawesome/free-solid-svg-icons';
import webVulnScanService from '../../services/webVulnScanService';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from 'react-toastify';
import FindingDetailsModal from './FindingDetailsModal';
import ScanResultsUrlTable from './ScanResultsUrlTable';
import './ScanResults.css';

const ScanResults = ({ scan, onRefresh }) => {
  const { t } = useLanguage();
  const { darkMode } = useTheme();
  const [findings, setFindings] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, critical, high, medium, low
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [viewMode, setViewMode] = useState('url'); // 'url' or 'findings'

  useEffect(() => {
    if (!scan?._id) return;
    loadFindings(); // initial load (shows spinner)

    // Poll findings in real time while the scan is still producing them, so newly
    // discovered weaknesses appear as they're found (background = no table blank).
    const running = ['running', 'queued', 'paused', 'pending'].includes(scan.status);
    if (!running) return;
    const interval = setInterval(() => loadFindings(true), 3000);
    return () => clearInterval(interval);
  }, [scan?._id, scan?.status]);

  const loadFindings = async (background = false) => {
    if (!background) setLoading(true);
    try {
      const data = await webVulnScanService.getResults(scan._id);
      setFindings(data);
    } catch (error) {
      console.error('Failed to load findings:', error);
      if (!background) toast.error(t('errors.loadFailed'));
    } finally {
      if (!background) setLoading(false);
    }
  };

  const handleCreateActions = async () => {
    if (selectedIds.size === 0) {
      toast.warning(t('webTesting.selectFindingsFirst'));
      return;
    }

    try {
      const result = await webVulnScanService.createActionsFromFindings(
        Array.from(selectedIds)
      );
      toast.success(t('webTesting.createdNActions', { count: result.created }));
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Failed to create actions:', error);
      toast.error(t('errors.actionCreationFailed'));
    }
  };

  const handleMarkDetectedBySIEM = async () => {
    if (selectedIds.size === 0) {
      toast.warning(t('webTesting.selectFindingsFirst'));
      return;
    }

    try {
      await webVulnScanService.updateSIEMCorrelation(
        Array.from(selectedIds),
        { detectedBySIEM: true, detectionMethod: 'siem_alert', detectionTimestamp: new Date() }
      );
      toast.success(t('webTesting.siemCorrelationUpdated'));
      loadFindings();
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Failed to update SIEM correlation:', error);
      toast.error(t('errors.updateFailed'));
    }
  };

  const handleMarkNotDetected = async () => {
    if (selectedIds.size === 0) {
      toast.warning(t('webTesting.selectFindingsFirst'));
      return;
    }

    try {
      await webVulnScanService.updateSIEMCorrelation(
        Array.from(selectedIds),
        { detectedBySIEM: false, detectionMethod: 'not_detected' }
      );
      toast.success(t('webTesting.siemCorrelationUpdated'));
      loadFindings();
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Failed to update SIEM correlation:', error);
      toast.error(t('errors.updateFailed'));
    }
  };

  const toggleSelection = (findingId) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(findingId)) {
      newSet.delete(findingId);
    } else {
      newSet.add(findingId);
    }
    setSelectedIds(newSet);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredFindings.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredFindings.map(f => f._id)));
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: '#dc2626',
      high: '#f97316',
      medium: '#eab308',
      low: '#3b82f6',
      informational: '#6b7280'
    };
    return colors[severity] || colors.informational;
  };

  const getSeverityIcon = (severity) => {
    if (severity === 'critical' || severity === 'high') {
      return <FontAwesomeIcon icon={faExclamationCircle} />;
    } else if (severity === 'medium') {
      return <FontAwesomeIcon icon={faExclamationTriangle} />;
    } else {
      return <FontAwesomeIcon icon={faInfoCircle} />;
    }
  };

  const countBySeverity = (severity) => {
    return findings.filter(f => f.severity === severity).length;
  };

  const filteredFindings = filter === 'all'
    ? findings
    : findings.filter(f => f.severity === filter);

  // Dark mode styles
  const styles = {
    container: {
      background: darkMode ? '#111827' : '#ffffff',
      color: darkMode ? '#f3f4f6' : '#1f2937',
      padding: '24px'
    },
    severityCard: {
      background: darkMode ? '#1f2937' : '#f9fafb',
      border: `2px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
      color: darkMode ? '#f3f4f6' : '#1f2937',
      transition: 'all 0.2s ease'
    },
    toolbar: {
      background: darkMode ? '#1f2937' : '#f9fafb',
      border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
      padding: '12px 16px',
      marginBottom: '16px',
      borderRadius: '6px'
    },
    table: {
      background: darkMode ? '#1f2937' : '#ffffff',
      border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
      borderRadius: '8px',
      overflow: 'hidden'
    },
    tableRow: {
      borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
      background: darkMode ? '#1f2937' : '#ffffff',
      transition: 'background 0.15s ease'
    },
    tableRowHover: {
      background: darkMode ? '#374151' : '#f9fafb'
    },
    tableHeader: {
      background: darkMode ? '#111827' : '#f9fafb',
      borderBottom: `2px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
      color: darkMode ? '#9ca3af' : '#6b7280',
      fontWeight: '600',
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      letterSpacing: '0.05em'
    },
    emptyState: {
      background: darkMode ? '#1f2937' : '#f9fafb',
      color: darkMode ? '#9ca3af' : '#6b7280',
      padding: '48px 24px',
      textAlign: 'center',
      borderRadius: '8px'
    }
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="scan-results" style={styles.container}>
      {/* View Mode Toggle */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
        padding: '8px',
        background: darkMode ? '#111827' : '#f9fafb',
        border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
        borderRadius: '8px',
        width: 'fit-content'
      }}>
        <button
          onClick={() => setViewMode('url')}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: 'none',
            background: viewMode === 'url'
              ? (darkMode ? '#3b82f6' : '#3b82f6')
              : 'transparent',
            color: viewMode === 'url'
              ? '#ffffff'
              : (darkMode ? '#9ca3af' : '#6b7280'),
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          <FontAwesomeIcon icon={faGlobe} />
          By URL
        </button>
        <button
          onClick={() => setViewMode('findings')}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: 'none',
            background: viewMode === 'findings'
              ? (darkMode ? '#3b82f6' : '#3b82f6')
              : 'transparent',
            color: viewMode === 'findings'
              ? '#ffffff'
              : (darkMode ? '#9ca3af' : '#6b7280'),
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          <FontAwesomeIcon icon={faList} />
          By Finding
        </button>
      </div>

      {/* Severity Summary */}
      <div className="severity-summary">
        <div
          className={`severity-card critical ${filter === 'critical' ? 'active' : ''}`}
          onClick={() => setFilter(filter === 'critical' ? 'all' : 'critical')}
          style={styles.severityCard}
        >
          <div className="count">{countBySeverity('critical')}</div>
          <div className="label">{t('blueTeam.critical')}</div>
        </div>
        <div
          className={`severity-card high ${filter === 'high' ? 'active' : ''}`}
          onClick={() => setFilter(filter === 'high' ? 'all' : 'high')}
          style={styles.severityCard}
        >
          <div className="count">{countBySeverity('high')}</div>
          <div className="label">{t('blueTeam.high')}</div>
        </div>
        <div
          className={`severity-card medium ${filter === 'medium' ? 'active' : ''}`}
          onClick={() => setFilter(filter === 'medium' ? 'all' : 'medium')}
          style={styles.severityCard}
        >
          <div className="count">{countBySeverity('medium')}</div>
          <div className="label">{t('blueTeam.medium')}</div>
        </div>
        <div
          className={`severity-card low ${filter === 'low' ? 'active' : ''}`}
          onClick={() => setFilter(filter === 'low' ? 'all' : 'low')}
          style={styles.severityCard}
        >
          <div className="count">{countBySeverity('low')}</div>
          <div className="label">{t('blueTeam.low')}</div>
        </div>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedIds.size > 0 && (
        <div className="bulk-toolbar" style={styles.toolbar}>
          <span className="selection-count">
            {t('common.nSelected', { count: selectedIds.size })}
          </span>
          <button onClick={handleCreateActions} className="btn-primary">
            {t('webTesting.createRedTeamActions')}
          </button>
          <button onClick={handleMarkDetectedBySIEM} className="btn-secondary">
            {t('webTesting.markDetectedBySIEM')}
          </button>
          <button onClick={handleMarkNotDetected} className="btn-secondary">
            {t('webTesting.markNotDetected')}
          </button>
        </div>
      )}

      {/* Content based on view mode */}
      {viewMode === 'url' ? (
        <ScanResultsUrlTable scan={scan} findings={findings} />
      ) : (
        <>
          {/* Findings Table */}
          {filteredFindings.length === 0 ? (
            <div className="empty-state" style={styles.emptyState}>
              <p>{t('webTesting.noFindings')}</p>
            </div>
          ) : (
        <div className="findings-table-container">
          <table className="findings-table" style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th className="checkbox-col">
                  <FontAwesomeIcon
                    icon={selectedIds.size === filteredFindings.length ? faCheckSquare : faSquare}
                    onClick={toggleSelectAll}
                    className="checkbox-icon"
                  />
                </th>
                <th>{t('common.severity')}</th>
                <th>{t('common.title')}</th>
                <th>{t('webTesting.url')}</th>
                <th>{t('webTesting.parameter')}</th>
                <th>{t('webTesting.confidence')}</th>
                <th>{t('mitre.technique')}</th>
                <th>{t('webTesting.siemDetection')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredFindings.map((finding) => (
                <tr
                  key={finding._id}
                  style={{ borderLeft: `4px solid ${getSeverityColor(finding.severity)}` }}
                  onClick={() => setSelectedFinding(finding)}
                  className={selectedIds.has(finding._id) ? 'selected' : ''}
                >
                  <td
                    className="checkbox-col"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelection(finding._id);
                    }}
                  >
                    <FontAwesomeIcon
                      icon={selectedIds.has(finding._id) ? faCheckSquare : faSquare}
                      className="checkbox-icon"
                    />
                  </td>
                  <td>
                    <span
                      className={`severity-badge ${finding.severity}`}
                      style={{ backgroundColor: getSeverityColor(finding.severity) }}
                    >
                      {getSeverityIcon(finding.severity)}
                      {finding.severity}
                    </span>
                  </td>
                  <td className="title-col">{finding.title}</td>
                  <td className="url-col">
                    <span title={finding.endpoint?.url}>
                      {finding.endpoint?.url}
                    </span>
                  </td>
                  <td>{finding.parameter?.name || '-'}</td>
                  <td>
                    <span className={`confidence-badge ${finding.confidence}`}>
                      {finding.confidence}
                    </span>
                  </td>
                  <td>
                    {finding.mitreMapping?.map((m, idx) => (
                      <span key={idx} className="mitre-badge" title={m.techniqueName}>
                        {m.techniqueId}
                      </span>
                    )) || '-'}
                  </td>
                  <td className="siem-col">
                    {finding.siemCorrelation?.detectedBySIEM ? (
                      <span className="siem-detected" title={finding.siemCorrelation.detectionMethod}>
                        ✓ Detected
                      </span>
                    ) : (
                      <span className="siem-not-detected">
                        ✗ Not Detected
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          )}
        </>
      )}

      {/* Finding Details Modal */}
      {selectedFinding && (
        <FindingDetailsModal
          finding={selectedFinding}
          onClose={() => setSelectedFinding(null)}
          onActionCreated={() => {
            loadFindings();
            if (onRefresh) onRefresh();
          }}
        />
      )}
    </div>
  );
};

export default ScanResults;
